import {
  EventType,
  isExitPlanModeToolInput,
  truncateAtWordBoundary,
} from '@/features/events/shared'
import type { ConversationTurnEntry } from '@/features/events/conversation/state/types'
import { ERRORED_TOOL_NAME } from '@/infra/sse/conversation/decryption/resolveNotificationContext'
import type { BlockingBucket } from '../types'
import {
  ERRORED_CONTEXT_SUFFIX,
  ERRORED_FALLBACK_INTENT,
  PLAN_INTENT,
  QUESTIONNAIRE_INTENT,
} from '../constants'
import { buildToolIntent, joinToolIntent, type ToolIntent } from './buildToolIntentLine'

/**
 * Structured blocking intent. `intent` is the flat string for aria-label and
 * tooltip; `toolName`/`detail` are present only when there is tool context to
 * render (tool-permission requests, errored events with surviving context).
 */
export interface BuiltIntent {
  readonly intent: string
  readonly toolName?: string
  readonly detail?: string
}

export function buildIntent(bucket: BlockingBucket, event: ConversationTurnEntry): BuiltIntent {
  switch (bucket) {
    case EventType.TOOL_PERMISSION_REQUEST:
      return buildToolPermissionIntent(event)
    case EventType.PLAN_APPROVAL_REQUEST:
      return buildPlanIntent(event)
    case EventType.AGENT_QUESTIONS:
      return { intent: QUESTIONNAIRE_INTENT }
    default:
      return buildErroredIntent(event)
  }
}

/**
 * Derives the plan branch intent. When the event carries an ExitPlanMode tool
 * input, the plan markdown's first heading line is surfaced as the intent so
 * users can identify the plan at a glance (e.g. "Plan: Say Hello in the
 * Terminal"). Any missing/malformed context falls back to the generic
 * PLAN_INTENT.
 */
function buildPlanIntent(event: ConversationTurnEntry): BuiltIntent {
  const context = event.notificationContext
  if (context && isExitPlanModeToolInput(context.toolInput)) {
    const title = extractPlanTitle(context.toolInput.plan)
    if (title) return { intent: title }
  }
  return { intent: PLAN_INTENT }
}

const PLAN_TITLE_MAX_LENGTH = 60

/**
 * Pulls a single-line title from plan markdown: the first non-empty line with
 * any leading markdown heading marker stripped, truncated at a word boundary.
 * Returns null only when there is no usable first line, so the caller can fall
 * back to the generic intent.
 */
function extractPlanTitle(plan: string): string | null {
  const cleaned = (plan.split('\n', 1)[0] ?? '')
    .trim()
    .replace(/^#+\s*/, '')
    .trim()
  if (!cleaned) return null
  return truncateAtWordBoundary(cleaned, PLAN_TITLE_MAX_LENGTH)
}

function buildToolPermissionIntent(event: ConversationTurnEntry): BuiltIntent {
  const context = event.notificationContext
  if (!context) return { intent: ERRORED_FALLBACK_INTENT }

  const toolIntent = buildToolIntent(context.toolName, context.toolInput)
  return toStructuredIntent(toolIntent, joinToolIntent(toolIntent))
}

function buildErroredIntent(event: ConversationTurnEntry): BuiltIntent {
  const context = event.notificationContext
  if (context && context.toolName !== ERRORED_TOOL_NAME) {
    const toolIntent = buildToolIntent(context.toolName, context.toolInput)
    const intent = `${joinToolIntent(toolIntent)}${ERRORED_CONTEXT_SUFFIX}`
    return toStructuredIntent(toolIntent, intent)
  }
  return { intent: ERRORED_FALLBACK_INTENT }
}

function toStructuredIntent(toolIntent: ToolIntent, intent: string): BuiltIntent {
  return {
    intent,
    toolName: toolIntent.toolName,
    ...(toolIntent.detail ? { detail: toolIntent.detail } : {}),
  }
}
